import React from "react";
import { LuArrowRight, LuCheck, LuMail, LuRotateCcw } from "react-icons/lu";
import { BusinessCaseResult } from "api/business_case/businessCaseApi.types";
import { useLeadForm } from "../lead-form/LeadFormProvider";
import { GhostButton, PrimaryButton } from "../ui";
import Disclosure from "./Disclosure";
import { Contact, decimal, money } from "./calculator";

export type EmailState = "idle" | "sending" | "sent" | "error";

type Props = {
  result: BusinessCaseResult;
  currency: string;
  contact: Contact;
  emailState: EmailState;
  emailError: string;
  onEmailRequest: () => void;
  onRestart: () => void;
};

function Metric({
  label,
  value,
  note,
  emphasis = false,
  className = "",
}: {
  label: string;
  value: string;
  note: string;
  emphasis?: boolean;
  className?: string;
}) {
  return (
    <div
      className={`tw-min-w-0 tw-rounded-2xl tw-border tw-p-5 ${
        emphasis ? "tw-border-[#087bd3] tw-bg-[#e9f4fd]" : "tw-border-[#c9deed] tw-bg-white"
      } ${className}`}
    >
      {/* Two lines are reserved whether or not this label needs them: the objective decides its
          wording, and a one-line label beside a two-line one drops its figure out of the row. */}
      <p className="tw-m-0 tw-min-h-8 tw-text-xs tw-font-black tw-uppercase tw-tracking-[.1em] tw-text-[#5b7188]">
        {label}
      </p>
      <p className="tw-mt-2 tw-mb-0 tw-text-3xl tw-font-black tw-tracking-[-.03em] tw-text-[#10233f] sm:tw-text-2xl">
        {value}
      </p>
      <p className="tw-mt-1 tw-mb-0 tw-text-xs tw-leading-5 tw-text-[#5b7188]">{note}</p>
    </div>
  );
}

// The quote card's price breakdown: what it is on the left, what it costs on the right. Navy only,
// since the card it lives in is the one dark surface on the step. The rule belongs to the group
// rather than the row, so the lines that make up one price are not divided from each other.
function LedgerRow({ label, note, figure }: { label: string; note?: string; figure: string }) {
  return (
    <div className="tw-flex tw-items-center tw-justify-between tw-gap-6 tw-py-3">
      <div className="tw-min-w-0">
        <p className="tw-m-0 tw-text-sm tw-font-bold tw-leading-6 tw-text-white">{label}</p>
        {note ? <p className="tw-mt-1 tw-mb-0 tw-text-xs tw-leading-5 tw-text-[#9db3c8]">{note}</p> : null}
      </div>
      <p className="tw-m-0 tw-shrink-0 tw-text-xl tw-font-black tw-tabular-nums tw-tracking-[-.02em] tw-text-white">
        {figure}
      </p>
    </div>
  );
}

// The rule above a total is heavier than the rules between the lines that add up to it, and on a
// phone the figure drops below its label: side by side it squeezed the label to a 15px column.
function LedgerTotal({ label, note, figure }: { label: string; note: string; figure: string }) {
  return (
    <div className="tw-flex tw-items-start tw-justify-between tw-gap-6 tw-border-t-2 tw-border-[#41668f] tw-pt-5 xs:tw-flex-col xs:tw-gap-2">
      {/* Nudged down 6px: aligning the boxes leaves the 12px label's cap sitting above the 36px
          figure's, because the gap between ascender and cap grows with the font size. */}
      <div className="tw-mt-1.5 tw-min-w-0 xs:tw-mt-0">
        <p className="tw-m-0 tw-text-xs tw-font-black tw-uppercase tw-tracking-[.12em] tw-text-white">
          {label}
        </p>
        <p className="tw-mt-2 tw-mb-0 tw-text-xs tw-leading-5 tw-text-[#9db3c8]">{note}</p>
      </div>
      <p className="tw-m-0 tw-shrink-0 tw-text-4xl tw-font-black tw-tabular-nums tw-tracking-[-.03em] tw-text-white sm:tw-text-3xl">
        {figure}
      </p>
    </div>
  );
}

function Working({ label, formula, substitution }: { label: string; formula: string; substitution: string }) {
  return (
    <div>
      <p className="tw-m-0 tw-text-sm tw-font-black tw-text-[#173754]">{label}</p>
      <p className="tw-mt-1 tw-mb-0 tw-text-sm tw-leading-6 tw-text-[#5b7188]">{formula}</p>
      <p className="tw-mt-2 tw-mb-0 tw-text-sm tw-font-bold tw-leading-6 tw-text-[#304d68]">{substitution}</p>
    </div>
  );
}

export default function StepResult({
  result,
  currency,
  contact,
  emailState,
  emailError,
  onEmailRequest,
  onRestart,
}: Props) {
  const { quote } = result;
  const openLeadForm = useLeadForm();

  return (
    <>
      <p className="tw-m-0 tw-text-xs tw-font-black tw-uppercase tw-tracking-[.15em] tw-text-[#087bd3]">
        3. Your mentoring business case
      </p>
      <h3 className="tw-mt-3 tw-mb-0 tw-text-3xl tw-font-black tw-leading-tight tw-tracking-[-.03em] tw-text-[#173754] sm:tw-text-2xl">
        {result.headline}
      </h3>
      <p className="tw-mt-4 tw-mb-0 tw-leading-7 tw-text-[#5b7188]">{result.narrative}</p>

      <div className="tw-mt-7 tw-grid tw-grid-cols-2 tw-gap-4 sm:tw-grid-cols-1">
        <Metric label={result.value_label} value={money(result.value)} note="Tied to the objective you chose." />
        <Metric
          label="Admin time saved value"
          value={money(result.admin_value)}
          note={`${decimal(result.admin_hours)} estimated hours`}
        />
        {/* Spanning the pair below them, and reset at `sm:` because a span of 2 in a one-column
            grid invents a second column rather than filling the row. */}
        <Metric
          label="Total Year 1 value"
          value={money(result.total_value)}
          note="Potential gross value before programme costs"
          emphasis
          className="tw-col-span-2 sm:tw-col-span-1"
        />
      </div>

      <p className="tw-mt-5 tw-mb-0 tw-text-xs tw-leading-5 tw-text-[#5b7188]">
        Scenario estimate, not a guaranteed outcome. This total is gross value, not net savings or ROI
        after fees. Time and productivity capacity do not necessarily become cash savings.
      </p>

      <div className="tw-mt-8 tw-rounded-2xl tw-bg-[#10233f] tw-p-7 tw-text-white sm:tw-p-5">
        <p className="tw-m-0 tw-text-xs tw-font-black tw-uppercase tw-tracking-[.12em] tw-text-[#70d9ce]">
          Instant planning quote
        </p>

        <p className="tw-mt-5 tw-mb-0 tw-text-2xl tw-font-black tw-tracking-[-.02em]">{quote.quote_name}</p>
        <p className="tw-mt-1 tw-mb-0 tw-text-sm tw-leading-6 tw-text-[#c2d3e2]">
          For {decimal(result.participants)} participants · mentors + mentees
        </p>

        {/* The setup fee used to be a footnote under the total. As its own line the price adds up in
            front of the reader instead of asking them to take the total on trust. */}
        <div className="tw-mt-6">
          {quote.first_year === null || quote.recurring === null ? null : (
            <div className="tw-border-t tw-border-[#2c4a6b] tw-pb-1 tw-pt-1">
              <LedgerRow
                label="Annual plan"
                note={`${quote.months} months`}
                figure={money(quote.recurring)}
              />
              <LedgerRow label="One-time setup" figure={money(quote.setup_fee)} />
            </div>
          )}

          <LedgerTotal
            label="Indicative first-year price"
            note={
              quote.first_year === null
                ? "Scope confirmed with FutureLab"
                : `All amounts in ${currency}`
            }
            figure={quote.first_year === null ? "Custom" : `~${money(quote.first_year)}`}
          />
        </div>

        <div className="tw-mt-7 tw-border-t tw-border-[#2c4a6b] tw-pt-6">
          <p className="tw-m-0 tw-text-xs tw-font-black tw-uppercase tw-tracking-[.12em] tw-text-[#70d9ce]">
            Illustrative plan inclusions
          </p>

          {/* One per line. The three-column grid broke every inclusion across two lines at every
              width the card is ever given, and three across never fit even once unwrapped. */}
          <ul className="tw-mt-4 tw-mb-0 tw-grid tw-list-none tw-gap-y-2 tw-p-0">
            {quote.includes.map((item) => (
              <li key={item} className="tw-flex tw-items-start tw-gap-2 tw-text-sm tw-leading-6 tw-text-[#c2d3e2]">
                <LuCheck aria-hidden className="tw-mt-1 tw-h-4 tw-w-4 tw-shrink-0 tw-text-[#70d9ce]" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <p className="tw-mt-7 tw-mb-0 tw-border-t tw-border-[#2c4a6b] tw-pt-5 tw-text-xs tw-leading-5 tw-text-[#9db3c8]">
          Indicative planning price only. Managed services, external mentor fees, travel, customisation,
          integrations and taxes are not included. Final scope and pricing require confirmation.
        </p>
      </div>

      <div className="tw-mt-6 tw-flex tw-flex-row tw-items-center tw-gap-3 sm:tw-flex-col sm:tw-items-stretch">
        <PrimaryButton
          type="button"
          size="lg"
          onClick={() =>
            openLeadForm({
              enquiryType: "business_case_demo",
              // Everything they already told us two screens ago. The business case roles are a
              // subset of the enquiry form's, so the value carries as-is; consent does not, because
              // it was given for the business case and this is a different request. The objective
              // and participant count are not prefilled: this variant asks neither, and both reach
              // sales through the summary below.
              prefill: {
                name: contact.name.trim(),
                email: contact.email.trim(),
                organization: contact.organization.trim(),
                company_size: contact.companySize,
                role: contact.role,
              },
              // The figures the visitor is looking at, carried into the request so the reply starts
              // from their case rather than asking them to restate it. Labelled as the card above
              // labels them, so the summary reads as the same numbers rather than a second set.
              estimateSummary: [
                { label: "Programme goal", value: result.objective_label },
                { label: "Programme size", value: `${decimal(result.participants)} participants` },
                { label: "Total Year 1 value", value: money(result.total_value) },
                {
                  label: "Indicative first-year price",
                  value: quote.first_year === null ? "Custom" : money(quote.first_year),
                },
              ],
            })
          }
          className="sm:tw-w-full"
        >
          Book a demo to lock this in
          <LuArrowRight aria-hidden className="tw-h-4 tw-w-4 tw-shrink-0" />
        </PrimaryButton>

        <PrimaryButton
          type="button"
          onClick={onEmailRequest}
          disabled={emailState === "sending" || emailState === "sent"}
          variant="inverse"
        >
          <LuMail aria-hidden className="tw-h-4 tw-w-4 tw-shrink-0" />
          {emailState === "sending" ? "Sending…" : null}
          {emailState === "sent" ? "Sent to your inbox" : null}
          {emailState === "idle" || emailState === "error" ? "Email me this business case" : null}
        </PrimaryButton>
      </div>

      <p aria-live="polite" className="tw-mt-3 tw-mb-0 tw-text-sm tw-leading-6 tw-text-[#5b7188]">
        {emailState === "sent"
          ? `Your business case and PDF quotation are on their way to ${contact.email.trim()}.`
          : null}
        {emailState === "error" ? emailError : null}
      </p>

      <Disclosure label="How we calculated this" className="tw-mt-7 tw-border-t tw-border-[#dce8f2] tw-pt-6">
        <div className="tw-grid tw-gap-5">
          <Working label={result.value_label} formula={result.formula} substitution={result.substitution} />
          <Working
            label="Universal admin-time value"
            formula={result.admin_formula}
            substitution={result.admin_substitution}
          />
          <Working
            label="Total Year 1 value"
            formula="Selected objective value + admin time saved value. Other objectives are not added to this total."
            substitution={result.total_formula}
          />

          <div>
            <p className="tw-m-0 tw-text-sm tw-font-black tw-text-[#173754]">Research basis for this outcome</p>
            <p className="tw-mt-1 tw-mb-0 tw-text-sm tw-leading-6 tw-text-[#5b7188]">{result.evidence.summary}</p>
            <p className="tw-mt-2 tw-mb-0 tw-text-sm tw-leading-6 tw-text-[#5b7188]">{result.evidence.caveat}</p>
            <ul className="tw-mt-3 tw-mb-0 tw-list-none tw-space-y-1 tw-p-0">
              {result.evidence.sources.map((source) => (
                <li key={source.url}>
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noreferrer"
                    className="tw-text-sm tw-font-bold tw-text-[#087bd3]"
                  >
                    {source.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="tw-m-0 tw-text-sm tw-font-black tw-text-[#173754]">Other planning assumptions</p>
            <ul className="tw-mt-2 tw-mb-0 tw-list-none tw-space-y-2 tw-p-0">
              {result.planning_notes.map((note) => (
                <li key={note} className="tw-text-sm tw-leading-6 tw-text-[#5b7188]">
                  {note}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Disclosure>

      <div className="tw-mt-7 tw-flex tw-flex-row tw-items-center tw-gap-4 sm:tw-flex-col sm:tw-items-stretch">
        <GhostButton type="button" onClick={onRestart} className="tw-shrink-0">
          <LuRotateCcw aria-hidden className="tw-h-4 tw-w-4" />
          Adjust my numbers
        </GhostButton>
        <p className="tw-m-0 tw-text-xs tw-leading-5 tw-text-[#5b7188]">
          Your details are kept for this visit, so you can recalculate without filling in the contact
          form again.
        </p>
      </div>
    </>
  );
}
